import { ENDED_TASK_STATUSES } from './constants';
import type { Optional } from 'lib/types/Optional';
import type { DeviceTaskStatusEnum } from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';

const isStoppedBeforeLastRoute = (stepCount: Optional<number>, stoppedStep: Optional<number>): boolean =>
  stoppedStep != null && stoppedStep < (stepCount ?? 0);

/**
 * Whether the whole run is over. A routine run reports a route count, and its task is marked DONE or FINISHED for every
 * route it completes before it carries on with the next one, so those statuses only end the run once the step has
 * reached the last route. The step can reach it before the task has moved on, while it is still named after the route
 * that just finished, so when the last route's name is known the task has to be named after it. A run without a route
 * count, or without a known step, is over once its task has ended. A robot that reports itself as cleaning has not
 * ended its run, whatever status its task has. A run known to have stopped before its last route (`stoppedStep`) has
 * not reached its end, even when its step moves on to the last route as the robot docks.
 */
export const hasRunEnded = (
  taskStatus: Optional<DeviceTaskStatusEnum>,
  step: Optional<number>,
  stepCount: Optional<number>,
  taskName?: Optional<string>,
  lastRouteName?: Optional<string>,
  isRobotCleaning?: boolean,
  stoppedStep?: Optional<number>
): boolean => {
  if (
    isRobotCleaning ||
    !taskStatus ||
    !ENDED_TASK_STATUSES.includes(taskStatus) ||
    isStoppedBeforeLastRoute(stepCount, stoppedStep)
  ) {
    return false;
  }

  if ((stepCount ?? 0) <= 0 || step == null) {
    return true;
  }

  const isNamedAfterAnotherRoute = !!taskName && !!lastRouteName && taskName !== lastRouteName;

  return step >= (stepCount ?? 0) && !isNamedAfterAnotherRoute;
};

/**
 * Whether the last route of the run is done. A routine run's step ends one above its route count once the last route
 * is done (before its task is marked as ended); any other run is done once its task has ended.
 */
export const isLastRouteDone = (
  taskStatus: Optional<DeviceTaskStatusEnum>,
  step: Optional<number>,
  stepCount: Optional<number>,
  taskName?: Optional<string>,
  lastRouteName?: Optional<string>,
  isRobotCleaning?: boolean,
  stoppedStep?: Optional<number>
): boolean =>
  hasRunEnded(taskStatus, step, stepCount, taskName, lastRouteName, isRobotCleaning, stoppedStep) ||
  ((stepCount ?? 0) > 0 &&
    (step ?? 1) > (stepCount ?? 0) &&
    !isRobotCleaning &&
    !isStoppedBeforeLastRoute(stepCount, stoppedStep));

/**
 * Whether a routine run was stopped before its last route (Send home, Cancel cleaning): the robot is no longer cleaning
 * and its task has ended, but not on the last route. Its step can't be trusted then, as the task may still move on.
 * A known `stoppedStep` before the last route settles it once the task has ended, even when the robot's status lags.
 */
export const hasRunStoppedEarly = (
  taskStatus: Optional<DeviceTaskStatusEnum>,
  step: Optional<number>,
  stepCount: Optional<number>,
  taskName?: Optional<string>,
  lastRouteName?: Optional<string>,
  isRobotCleaning?: boolean,
  stoppedStep?: Optional<number>
): boolean => {
  const hasTaskEnded = !!taskStatus && ENDED_TASK_STATUSES.includes(taskStatus);

  if (isStoppedBeforeLastRoute(stepCount, stoppedStep)) {
    return hasTaskEnded;
  }

  return (
    !isRobotCleaning &&
    (stepCount ?? 0) > 0 &&
    hasTaskEnded &&
    !hasRunEnded(taskStatus, step, stepCount, taskName, lastRouteName, isRobotCleaning)
  );
};
